import React, { useState as useS, useEffect as useE } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons as Icn } from './icons.jsx';
import { MapProductsStep, WarehouseInventoryStep } from './uploadComponents.jsx';
import { FinishStep } from './finishStep.jsx';
import { Popover, Tooltip, notification } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import { FiLogOut } from 'react-icons/fi';
import { SignOut, UpdateBusinessOnboarding } from '../../slices/auth';
import { getDefaultLandingPath } from '../../utils/getDefaultLandingPath';
import { HeaderWrapper } from '../header/style';
import './business-onboarding.css';

const S = { MapProductsStep, WarehouseInventoryStep, FinishStep };

// Wizard steps. Sellers (role 2) don't manage warehouse inventory, so their flow is just
// Map Products → Finish; everyone else (e.g. owners, role 5) also gets the Inventory step.
const ALL_STEP_DEFS = [
{ key: "mapping", title: "Map Products" },
{ key: "inventory", title: "Inventory" },
{ key: "finish", title: "Finish" }];

const stepDefsForRole = (roleId) =>
roleId === 2 ? ALL_STEP_DEFS.filter((s) => s.key !== "inventory") : ALL_STEP_DEFS;

function ProgressIndicator({ stepIndex, steps }) {
  const cur = stepIndex + 1; // 1-based
  return (
    <div className="progress-indicator">
      <div className="progress-container">
        <div className="progress-steps" style={{ gap: "99px" }}>
          {steps.map((step, index) => {
            const id = index + 1;
            const cls = id < cur ? "completed" : id === cur ? "current" : "pending";
            return (
              <div key={step.key} className="progress-step">
                <div className="progress-step-content">
                  <div className={`progress-step-circle ${cls}`}>
                    {id < cur ? <Icn.Check size={16} /> : <span>{id}</span>}
                  </div>
                  <span className="progress-step-title">{step.title}</span>
                </div>
                {index < steps.length - 1 &&
                <div className="progress-connector">
                    <div className="progress-line" style={{ width: "14px" }}>
                      <div className={`progress-line-fill ${id < cur ? "w-full" : "w-0"}`} />
                    </div>
                  </div>
                }
              </div>);

          })}
        </div>
      </div>
    </div>);

}

const DEFAULT_STATE = {
  mappingUploaded: false,
  inventoryUploaded: false
};

function App() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentUser, parentUser } = useSelector((state) => state?.auth || {});

  // Real user drawer — mirror the app header: impersonated parent takes precedence, initials from full name
  const acctUser = !isEmpty(parentUser) ? parentUser : currentUser;
  const nameParts = acctUser?.full_name?.toUpperCase()?.split(' ') || [];
  const abbreviatedName = `${nameParts?.[0]?.[0] || ''}${nameParts?.[1]?.[0] || ''}`;

  const UserMenu = () => (
    <HeaderWrapper isNewHeader={true}>
      <div className="user-dropdown">
        <div className="d-flex grid-column-gap-10 marginy-8">
          <div><span className="icon-user fs-16"></span></div>
          <div className="d-flex flex-column grid-column-2">
            <Tooltip title={acctUser?.full_name}>
              <span className="username fs-14 tw-whitespace-nowrap tw-max-w-[120px] tw-truncate">{acctUser?.full_name}</span>
            </Tooltip>
            <Tooltip title={acctUser?.email}>
              <span className="email tw-max-w-[120px] tw-truncate">{acctUser?.email}</span>
            </Tooltip>
            <span className="role color-secondary fs-14">{acctUser?.company?.name}</span>
          </div>
        </div>
        <hr />
        <div onClick={() => dispatch(SignOut())} className="logout grid-column-gap-10 d-flex">
          <div><FiLogOut className="color-primary" /></div>
          <span>Logout</span>
        </div>
      </div>
    </HeaderWrapper>
  );

  // Sellers (role 2) skip the warehouse-inventory step entirely.
  const roleId = currentUser?.role?.id;
  const skipInventory = roleId === 2;
  const STEP_DEFS = stepDefsForRole(roleId);
  const LAST = STEP_DEFS.length - 1;

  // DB-backed progress: initialise from the user's saved step/state (the wizard is resumable).
  const parseState = (raw) => {
    if (!raw) return DEFAULT_STATE;
    if (typeof raw === 'string') { try { return JSON.parse(raw); } catch (e) { return DEFAULT_STATE; } }
    return raw;
  };
  // A step counts complete only when its required upload was done. The inventory step only exists
  // for non-sellers, so for sellers completion hinges on the mapping upload alone (finish has no upload).
  const uploadDone = (st, key) =>
  key === 'mapping' ? !!st?.mappingUploaded : key === 'inventory' ? !!st?.inventoryUploaded : true;
  const allComplete = (st) => STEP_DEFS.every((d) => uploadDone(st, d.key));
  const firstIncomplete = (st) => {
    const i = STEP_DEFS.findIndex((d) => !uploadDone(st, d.key));
    return i === -1 ? LAST : i;
  };

  const [step, setStep] = useS(() => {
    const savedState = parseState(currentUser?.business_onboarding_state);
    const savedStep = Number(currentUser?.business_onboarding_step) || 0;
    // Resume at the first incomplete step. e.g. mapping done + inventory skipped -> land on Inventory
    // (step 2), NOT back on Map Products. Only honor a saved later position once everything is complete.
    if (!allComplete(savedState)) return firstIncomplete(savedState);
    return Math.min(savedStep, LAST); // clamp: a stale index from a longer flow can exceed this role's LAST
  });
  const [state, setState] = useS(() => parseState(currentUser?.business_onboarding_state));

  // Business onboarding is only for sellers/owners (role 2 or 5) — same roles as the client onboarding.
  // Anyone else (e.g. warehouse role 3) goes to their usual home instead of seeing this flow.
  useE(() => {
    if (roleId && roleId !== 2 && roleId !== 5) {
      navigate(roleId === 3 ? '/warehouse-location' : '/inventory/sku', { replace: true });
    }
  }, [roleId]);

  // already completed -> route is gated; bounce to the app (App.js also guards this).
  useE(() => {
    if (currentUser?.business_onboarding_completed) navigate(getDefaultLandingPath(currentUser), { replace: true });
  }, [currentUser?.business_onboarding_completed]);

  const persist = (partial) => dispatch(UpdateBusinessOnboarding(partial));

  // every step's data + position is saved to the DB as it changes
  const set = (patch) => {
    const nextState = { ...state, ...patch };
    setState(nextState);
    persist({ state: nextState });
  };
  const goToStep = (s) => {
    const clamped = Math.max(0, Math.min(s, LAST));
    setStep(clamped);
    persist({ step: clamped });
  };
  const next = () => goToStep(step + 1);
  // A step is "done" when its required upload succeeded. Completed steps are LOCKED — Back skips over
  // them and only returns to an earlier step that was skipped/not completed.
  const isStepDone = (i) => uploadDone(state, STEP_DEFS[i]?.key);
  const prevOpenStep = (from) => {
    for (let i = from - 1; i >= 0; i--) { if (!isStepDone(i)) return i; }
    return -1;
  };
  const back = () => {
    const target = prevOpenStep(step);
    if (target >= 0) goToStep(target);
  };
  const skip = () => {
    // skipping the product-sku step: warn that initial inventory depends on products existing
    // (only relevant when this role actually has an inventory step after mapping)
    if (step === 0 && !skipInventory) {
      notification.warning({
        message: 'Product setup skipped',
        description: 'Initial inventory can only be uploaded for products that exist. Use Back to complete "Map SKUs & products" first, or your inventory upload may not match anything.',
        top: 65,
        duration: 8
      });
    }
    goToStep(step + 1);
  };

  // terminal action — "Enter MarketOS" on the finish screen.
  // All steps done -> completed (route gated, can't return). Skipped steps -> mark walked-through
  // (reuses the exited flag) so login won't force them back, but they CAN return to finish later.
  // Must await the persist before navigating: App.js's onboarding gate re-checks
  // completed/exited on the new route immediately, and if it still sees the pre-update (stale)
  // Redux flags it bounces straight back to /business-onboarding — the fire-and-forget version of
  // this let a single "Enter MarketOS" click land you back on step 1 instead of the dashboard.
  const finish = async () => {
    const partial = allComplete(state) ? { completed: true } : { exited: true };
    try {
      await dispatch(UpdateBusinessOnboarding(partial)).unwrap();
      navigate(getDefaultLandingPath(currentUser));
    } catch (e) {
      notification.error({
        message: 'Could not finish setup',
        description: 'Please try again.',
        top: 65
      });
    }
  };

  const common = { state, set, onNext: next, onBack: back, onSkip: skip };

  const FINISH_ITEMS = [
  { ok: state.mappingUploaded, label: state.mappingUploaded ? "Product & vendor mapping uploaded" : "Mapping skipped" },
  ...skipInventory ? [] : [{ ok: state.inventoryUploaded, label: state.inventoryUploaded ? "Warehouse inventory uploaded" : "Inventory skipped" }]];


  // hide Back when there's no earlier step left to return to (all earlier steps are completed/locked)
  const backHidden = prevOpenStep(step) < 0;

  // Inventory depends on products existing — lock the Inventory step until Map Products is complete.
  const inventoryLocked = !skipInventory && !uploadDone(state, 'mapping');

  const currentKey = STEP_DEFS[step]?.key;
  let body;
  if (currentKey === "mapping") body = <S.MapProductsStep {...common} backHidden={backHidden} soloStep={skipInventory} />;else
  if (currentKey === "inventory") body = <S.WarehouseInventoryStep {...common} backHidden={backHidden} locked={inventoryLocked} />;else
  body = <S.FinishStep state={state} items={FINISH_ITEMS} onEnter={finish} />;

  return (
    <div className="onboarding-wizard">
      <div className="topbar">
        {/* same brand mark as the app sidebar (.logo-text): "Market" navy + "OS" blue accent */}
        <span
          className="ob-logo-text"
          onClick={() => navigate(getDefaultLandingPath(currentUser))}
          style={{ fontSize: "18px", fontWeight: 700, color: "#1B2559", letterSpacing: "-0.3px", cursor: "pointer", userSelect: "none" }}
        >
          Market<span style={{ color: "#1565D8" }}>OS</span>
        </span>
        <div className="acct">
          <Popover trigger="click" placement="bottomRight" content={<UserMenu />}>
            <span className="avatar" style={{ cursor: "pointer" }}>{abbreviatedName}</span>
          </Popover>
        </div>
      </div>

      <div className="onboarding-content">
        <div className="onboarding-container">
          <div className="header">
            <h1 className="title">Set up your <span className="brand">catalog & inventory</span></h1>
            <p className="subtitle">{skipInventory ? "Map your products in a few steps" : "Map your products and import your warehouse inventory in a few steps"}</p>
          </div>

          {step < LAST && <ProgressIndicator stepIndex={step} steps={STEP_DEFS} />}

          <div className="step-content">{body}</div>
        </div>
      </div>
    </div>);

}

export default App;
